import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  LinearProgress,
  Alert,
  Chip,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  MdSearch,
  MdRefresh,
  MdInfoOutline,
  MdFileDownload,
  MdGridOn,
  MdPrint,
  MdLocalOffer,
  MdCurrencyRupee,
  MdEmojiEvents,
  MdRemoveShoppingCart,
  MdAssignmentReturn,
  MdTrendingUp,
  MdShoppingBag,
  MdStar,
  MdClose,
} from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import {
  FONT,
  inr,
  toISO,
  authToken,
  PRESETS,
  presetRange,
  rangeLabel,
  StatCard,
  statGridSx,
  exportButtonSx,
  downloadCsv,
  downloadExcel,
  openPrintView,
} from './reportShared';

const REPORT_ROUTE = 'reports/brand-performance';
const pill = (bg, fg) => ({ fontWeight: 700, fontFamily: FONT, background: bg, color: fg, height: 24, fontSize: 11 });
const dash = <Typography component="span" sx={{ color: '#94a3b8' }}>—</Typography>;

const returnChip = (rate, units) => {
  if (!units) return dash;
  const [bg, fg] = rate >= 10 ? ['#fee2e2', '#b91c1c'] : rate >= 5 ? ['#ffedd5', '#9a3412'] : ['#f1f5f9', '#475569'];
  return <Chip size="small" label={`${units} · ${rate}%`} sx={pill(bg, fg)} />;
};

const trendChip = (r) => {
  if (r.trendNew) return <Chip size="small" label="New" sx={pill('#dbeafe', '#1d4ed8')} />;
  if (r.trendPct === null || r.trendPct === undefined) return dash;
  if (r.trendPct === 0) return <Chip size="small" label="0%" sx={pill('#f1f5f9', '#475569')} />;
  const up = r.trendPct > 0;
  return <Chip size="small" label={`${up ? '▲' : '▼'} ${Math.abs(r.trendPct)}%`} sx={pill(up ? '#dcfce7' : '#fee2e2', up ? '#166534' : '#b91c1c')} />;
};
const trendText = (r) => (r.trendNew ? 'New' : r.trendPct === null || r.trendPct === undefined ? '' : `${r.trendPct}%`);

const ratingCell = (r) =>
  r.avgRating === null || r.avgRating === undefined ? (
    dash
  ) : (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: r.avgRating >= 4 ? '#15803d' : r.avgRating >= 3 ? '#b45309' : '#b91c1c', fontWeight: 700 }}>
      <MdStar size={16} />
      {r.avgRating}
      <Typography component="span" sx={{ fontSize: 11.5, color: '#64748b', fontWeight: 500, fontFamily: FONT }}>({r.reviews})</Typography>
    </Box>
  );

const shareBar = (r) => (
  <Box sx={{ minWidth: 110, display: 'flex', alignItems: 'center', gap: 1 }}>
    <LinearProgress
      variant="determinate"
      value={Math.min(r.sharePct || 0, 100)}
      sx={{ flex: 1, height: 8, borderRadius: 4, background: '#e2e8f0', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#f59e0b,#ea580c)', borderRadius: 4 } }}
    />
    <Typography sx={{ fontSize: 11, fontWeight: 700, fontFamily: FONT, minWidth: 40, textAlign: 'right' }}>{r.sharePct}%</Typography>
  </Box>
);

const marginCell = (r) =>
  r.profit === null || r.profit === undefined ? (
    dash
  ) : (
    <Box sx={{ lineHeight: 1.25, color: r.profit >= 0 ? '#15803d' : '#b91c1c', fontWeight: 700 }}>
      {inr(r.profit)}
      <Typography component="div" sx={{ fontSize: 11.5, fontWeight: 600, fontFamily: FONT }}>{r.marginPct}%</Typography>
    </Box>
  );
const marginText = (r) => (r.profit === null || r.profit === undefined ? '' : `${r.profit} (${r.marginPct}%)`);
const marginPrint = (r) => (r.profit === null || r.profit === undefined ? '-' : `${inr(r.profit)} (${r.marginPct}%)`);

const BrandCell = ({ r }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 210 }}>
    <Avatar variant="rounded" src={r.image || undefined} alt={r.title} sx={{ width: 38, height: 38, bgcolor: '#e2e8f0', color: '#475569', fontWeight: 700, fontSize: 13.5 }}>
      {(r.title || '?').charAt(0).toUpperCase()}
    </Avatar>
    <Box sx={{ lineHeight: 1.25, minWidth: 0 }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, fontFamily: FONT }}>{r.title}</Typography>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {r.status === 'inactive' && <Chip size="small" label="Inactive" sx={{ ...pill('#f1f5f9', '#64748b'), height: 20, fontSize: 11 }} />}
        {!r.listed && !r.pseudo && <Chip size="small" label="Not in brand list" sx={{ ...pill('#fef3c7', '#92400e'), height: 20, fontSize: 11 }} />}
      </Box>
    </Box>
  </Box>
);

const col = (key, label, opts = {}) => ({ key, label, ...opts });
const money = (key, label, extra = {}) => col(key, label, { numeric: true, text: (r) => r[key], cell: (r) => inr(r[key]), print: (r) => inr(r[key]), ...extra });
const num = (key, label, extra = {}) => col(key, label, { numeric: true, text: (r) => r[key], ...extra });
const brandNameCol = col('title', 'Brand', { cell: (r) => <BrandCell r={r} />, text: (r) => r.title, extra: (r) => ({ Status: r.status || (r.pseudo ? '' : 'Not in brand list') }) });

const TABS = [
  {
    key: 'brand',
    label: 'By Brand',
    defaultSort: 'revenue_desc',
    clickable: true,
    sorts: [
      ['revenue_desc', 'Revenue: high to low'],
      ['revenue_asc', 'Revenue: low to high'],
      ['units_desc', 'Units sold: high to low'],
      ['units_asc', 'Units sold: low to high'],
      ['margin_desc', 'Margin: high to low'],
      ['growth_desc', 'Growth: highest first'],
      ['growth_asc', 'Growth: lowest first'],
      ['return_desc', 'Return rate: high to low'],
      ['rating_desc', 'Rating: high to low'],
      ['name_asc', 'Name: A to Z'],
    ],
    columns: [
      col('rank', '#', { center: true, cell: (r, i) => i, text: (r, i) => i, skipExport: true }),
      brandNameCol,
      num('products', 'Products'),
      num('productsSold', 'Products Sold'),
      num('orders', 'Orders'),
      num('unitsSold', 'Units Sold', { bold: true }),
      money('revenue', 'Revenue', { bold: true }),
      col('sharePct', '% of Total', { cell: shareBar, text: (r) => `${r.sharePct}%` }),
      money('avgOrderValue', 'Avg Order Value'),
      col('returns', 'Returned', { center: true, text: (r) => `${r.returnedUnits} (${r.returnRate}%)`, cell: (r) => returnChip(r.returnRate, r.returnedUnits) }),
      col('rating', 'Rating', { center: true, text: (r) => (r.avgRating === null ? '' : `${r.avgRating} (${r.reviews})`), cell: ratingCell }),
      col('margin', 'Est. Profit / Margin', { numeric: true, text: marginText, cell: marginCell, print: marginPrint }),
      money('stockValue', 'Stock Value'),
      col('trend', 'Trend', { center: true, text: trendText, cell: trendChip }),
    ],
  },
  {
    key: 'lowsales',
    label: 'Low / No Sales',
    defaultSort: 'stockvalue_desc',
    sorts: [
      ['stockvalue_desc', 'Stock value: high to low'],
      ['units_asc', 'Units sold: low to high'],
      ['name_asc', 'Name: A to Z'],
    ],
    columns: [
      brandNameCol,
      num('products', 'Products'),
      num('productsSold', 'Products Sold'),
      num('unitsSold', 'Units Sold (period)', { bold: true }),
      num('stock', 'Stock'),
      money('stockValue', 'Stock Value', { bold: true }),
    ],
  },
];

const BrandReport = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const tab = TABS[tabIndex];

  const [preset, setPreset] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [returnsMode, setReturnsMode] = useState('include');
  const [sortBy, setSortBy] = useState(TABS[0].defaultSort);
  const [maxUnits, setMaxUnits] = useState('2');
  const [stockMode, setStockMode] = useState('in');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const baseParams = useMemo(() => {
    const p = { returns: returnsMode };
    if (from) p.from = from;
    if (to) p.to = to;
    if (source) p.source = source;
    return p;
  }, [from, to, returnsMode, source]);

  const filterParams = useMemo(() => {
    const p = { ...baseParams, tab: tab.key, sortBy };
    if (search) p.search = search;
    if (status) p.status = status;
    if (tab.key === 'lowsales') {
      p.maxUnits = maxUnits;
      p.stock = stockMode;
    }
    return p;
  }, [baseParams, tab.key, sortBy, search, status, maxUnits, stockMode]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, page: page + 1, limit: rowsPerPage }, authToken(), false);
      if (res?.success) setData(res);
      else setError(res?.message || 'Failed to load report');
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, rowsPerPage]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const goToTab = (index, overrides = {}) => {
    setTabIndex(index);
    setSortBy(TABS[index].defaultSort);
    setPage(0);
    if (overrides.maxUnits !== undefined) setMaxUnits(overrides.maxUnits);
    if (overrides.stockMode !== undefined) setStockMode(overrides.stockMode);
  };

  const applyPreset = (key) => {
    const range = presetRange(key);
    setPreset(key);
    setFrom(range.from);
    setTo(range.to);
    setPage(0);
  };

  const onDateChange = (setter) => (e) => {
    setter(e.target.value);
    setPreset('');
    setPage(0);
  };

  const setFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };

  const resetFilters = () => {
    setPreset('all');
    setFrom('');
    setTo('');
    setSearchInput('');
    setSearch('');
    setStatus('');
    setSource('');
    setReturnsMode('include');
    setSortBy(tab.defaultSort);
    setMaxUnits('2');
    setStockMode('in');
    setPage(0);
  };

  const openDetail = async (row) => {
    setDetail({ loading: true, title: row.title });
    setDetailLoading(true);
    try {
      const res = await getData(REPORT_ROUTE, { ...baseParams, detail: row.brandKey }, authToken(), false);
      if (res?.detail) setDetail({ ...res.detail, title: row.title });
      else {
        setDetail(null);
        setError(res?.message || 'Could not load brand details');
      }
    } catch (err) {
      setDetail(null);
      setError(err.message || 'Could not load brand details');
    } finally {
      setDetailLoading(false);
    }
  };

  const exportColumns = tab.columns.filter((c) => !c.skipExport);
  const exportObjects = (rows) =>
    rows.map((r) => {
      const obj = {};
      for (const c of exportColumns) {
        obj[c.label] = c.text(r);
        if (c.extra) Object.assign(obj, c.extra(r));
      }
      return obj;
    });
  const fileStamp = () => `Brand_Report_${tab.label.replace(/[^A-Za-z]+/g, '_')}_${from || 'all'}_${to || toISO(new Date())}`;

  const runExport = async (kind) => {
    setExporting(true);
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, export: 'all' }, authToken(), false);
      if (!res?.success) throw new Error(res?.message || 'Export failed');
      const rows = res.rows || [];
      if (kind === 'csv') downloadCsv(exportObjects(rows), fileStamp());
      else if (kind === 'excel') downloadExcel(exportObjects(rows), fileStamp(), tab.label);
      else
        openPrintView({
          title: `Brand Report — ${tab.label}`,
          subtitle: `${rangeLabel(from, to)} · ${rows.length} brands`,
          headers: exportColumns.map((c) => c.label),
          cells: rows.map((r) => exportColumns.map((c) => ({ value: c.print ? c.print(r) : c.text(r), className: c.numeric ? 'num' : '' }))),
        });
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const stats = data?.stats || {};
  const total = data?.meta?.total || 0;
  const rows = data?.tab === tab.key ? data.rows || [] : [];
  const baseIndex = page * rowsPerPage;
  const marginKnown = stats.marginPct !== null && stats.marginPct !== undefined;
  const ratingKnown = stats.avgRating !== null && stats.avgRating !== undefined;

  const cards = [
    { label: 'Active Brands', value: (stats.activeBrands || 0).toLocaleString('en-IN'), icon: MdLocalOffer, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: `${stats.brandsWithSales || 0} had sales` },
    { label: 'Units Sold', value: (stats.unitsSold || 0).toLocaleString('en-IN'), icon: MdShoppingBag, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: returnsMode === 'exclude' ? 'Net of returns' : 'Returns included' },
    { label: 'Brand Revenue', value: inr(stats.revenue), icon: MdCurrencyRupee, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: 'Incl. GST' },
    { label: 'Top Brand', value: stats.topBrand?.title || '—', icon: MdEmojiEvents, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: stats.topBrand ? `${stats.topBrand.sharePct}% of revenue` : 'No sales' },
    { label: 'No-Sale Brands', value: (stats.noSaleBrands || 0).toLocaleString('en-IN'), icon: MdRemoveShoppingCart, gradient: 'linear-gradient(135deg,#64748b,#334155)', sub: 'Click to review', onClick: () => goToTab(1, { maxUnits: '0', stockMode: 'all' }), active: tab.key === 'lowsales' && maxUnits === '0' && stockMode === 'all' },
    { label: 'Return Rate', value: `${stats.returnRate || 0}%`, icon: MdAssignmentReturn, gradient: 'linear-gradient(135deg,#f43f5e,#dc2626)', sub: `${stats.returnedUnits || 0} units returned` },
    { label: 'Avg Rating', value: ratingKnown ? `${stats.avgRating} ★` : '—', icon: MdStar, gradient: 'linear-gradient(135deg,#eab308,#ca8a04)', sub: ratingKnown ? `${stats.reviews} ratings` : 'No ratings in period' },
    { label: 'Est. Margin', value: marginKnown ? `${stats.marginPct}%` : '—', icon: MdTrendingUp, gradient: 'linear-gradient(135deg,#ec4899,#db2777)', sub: marginKnown ? `Profit ${inr(stats.profit)}` : 'No purchase cost recorded' },
  ];

  const activeFilterCount = [from || to, search, status, source, returnsMode !== 'include', sortBy !== tab.defaultSort].filter(Boolean).length;

  const detailMini = (title, headers, body) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontWeight: 800, fontFamily: FONT, mb: 1 }}>{title}</Typography>
      <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: '10px' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {headers.map((h, i) => (
                <TableCell key={h} align={i === 0 ? 'left' : 'right'} sx={{ fontWeight: 800, background: '#f1f5f9', fontFamily: FONT, whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{body}</TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
  const emptyRow = (span, text) => (
    <TableRow><TableCell colSpan={span} sx={{ textAlign: 'center', color: '#94a3b8', py: 3 }}>{text}</TableCell></TableRow>
  );

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, fontFamily: FONT }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 23.5, fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Brand Report</Typography>
          <Typography sx={{ fontSize: 12.5, color: '#64748b', fontFamily: FONT }}>Brand sales, returns, ratings and stock — click a brand for its breakdown</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<MdInfoOutline />} onClick={() => setNotesOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', fontFamily: FONT }}>Note</Button>
          <Button startIcon={<MdFileDownload />} disabled={exporting || !total} onClick={() => runExport('csv')} sx={exportButtonSx('#16a34a', '#15803d')}>CSV</Button>
          <Button startIcon={<MdGridOn />} disabled={exporting || !total} onClick={() => runExport('excel')} sx={exportButtonSx('#0d9488', '#0f766e')}>Excel</Button>
          <Button startIcon={<MdPrint />} disabled={exporting || !total} onClick={() => runExport('print')} sx={exportButtonSx('#7c3aed', '#6d28d9')}>Print / PDF</Button>
        </Box>
      </Box>

      <Box sx={statGridSx}>
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>
            Filters {activeFilterCount > 0 && <Chip size="small" label={`${activeFilterCount} active`} color="primary" sx={{ ml: 1, fontWeight: 700 }} />}
          </Typography>
          <Button size="small" startIcon={<MdRefresh />} onClick={resetFilters} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Reset</Button>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {PRESETS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              onClick={() => applyPreset(p.key)}
              sx={{
                fontWeight: 700,
                fontFamily: FONT,
                background: preset === p.key ? 'rgb(255 209 2)' : '#f1f5f9',
                color: '#0f172a',
                border: preset === p.key ? '1px solid #0f172a' : '1px solid transparent',
                '&:hover': { background: preset === p.key ? 'rgb(255 209 2)' : '#e2e8f0' },
              }}
            />
          ))}
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="From" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={from} onChange={onDateChange(setFrom)} inputProps={{ max: to || undefined }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={to} onChange={onDateChange(setTo)} inputProps={{ min: from || undefined }} />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <TextField
              label="Search brand"
              size="small"
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><MdSearch size={18} /></InputAdornment>) }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField select label="Brand status" size="small" fullWidth value={status} onChange={setFilter(setStatus)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={3}>
            <TextField select label="Product source" size="small" fullWidth value={source} onChange={setFilter(setSource)}>
              <MenuItem value="">All products</MenuItem>
              <MenuItem value="own">Own products</MenuItem>
              <MenuItem value="seller">Seller products</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField select label="Sort by" size="small" fullWidth value={sortBy} onChange={setFilter(setSortBy)}>
              {tab.sorts.map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <TextField select label="Returns" size="small" fullWidth value={returnsMode} onChange={setFilter(setReturnsMode)}>
              <MenuItem value="include">Include returned units</MenuItem>
              <MenuItem value="exclude">Net of returns</MenuItem>
            </TextField>
          </Grid>
          {tab.key === 'lowsales' && (
            <>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField select label="Units sold in period" size="small" fullWidth value={maxUnits} onChange={setFilter(setMaxUnits)}>
                  <MenuItem value="0">No sales (0)</MenuItem>
                  <MenuItem value="1">1 or fewer</MenuItem>
                  <MenuItem value="2">2 or fewer</MenuItem>
                  <MenuItem value="5">5 or fewer</MenuItem>
                  <MenuItem value="10">10 or fewer</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField select label="Stock" size="small" fullWidth value={stockMode} onChange={setFilter(setStockMode)}>
                  <MenuItem value="in">In stock only</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </TextField>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Tabs + table */}
      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => goToTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            borderBottom: '1px solid #e2e8f0',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontFamily: FONT, minHeight: 56 },
            '& .Mui-selected': { color: '#0f172a !important' },
            '& .MuiTabs-indicator': { background: 'rgb(255 209 2)', height: 4, borderRadius: 2 },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>{tab.label}</Typography>
          <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>
            {total.toLocaleString('en-IN')} brands
            {tab.key === 'lowsales' && stats.lowSalesStockValue !== undefined && <> · <b>{inr(stats.lowSalesStockValue)}</b> stock value</>}
            {tab.clickable && ' · click a row for details'}
          </Typography>
        </Box>
        {(loading || exporting) && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {tab.columns.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.center ? 'center' : c.numeric ? 'right' : 'left'}
                    sx={{ fontWeight: 800, fontFamily: FONT, background: '#0f172a', color: '#fff', whiteSpace: 'nowrap', py: 1.5 }}
                  >
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tab.columns.length} sx={{ textAlign: 'center', color: '#94a3b8', py: 6, fontFamily: FONT }}>
                    Nothing matches the selected filters.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r, idx) => (
                <TableRow
                  key={`${r.brandKey}-${idx}`}
                  hover
                  onClick={tab.clickable ? () => openDetail(r) : undefined}
                  sx={{ cursor: tab.clickable ? 'pointer' : 'default', '&:nth-of-type(even)': { background: '#f8fafc' } }}
                >
                  {tab.columns.map((c) => (
                    <TableCell
                      key={c.key}
                      align={c.center ? 'center' : c.numeric ? 'right' : 'left'}
                      sx={{ fontFamily: FONT, whiteSpace: 'nowrap', fontSize: 11.5, fontWeight: c.bold ? 700 : 500, color: '#334155' }}
                    >
                      {c.cell ? c.cell(r, baseIndex + idx + 1) : c.text(r, baseIndex + idx + 1)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={Math.min(page, Math.max(Math.ceil(total / rowsPerPage) - 1, 0))}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </Paper>

      {/* Brand drill-down */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {detail?.title}
          <IconButton onClick={() => setDetail(null)} size="small"><MdClose /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading || !detail?.brand ? (
            <LinearProgress />
          ) : (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
                <Chip label={`Revenue ${inr(detail.brand.revenue)}`} sx={pill('#ffedd5', '#9a3412')} />
                <Chip label={`${detail.brand.unitsSold} units`} sx={pill('#e0f2fe', '#075985')} />
                <Chip label={`${detail.brand.orders} orders`} sx={pill('#ede9fe', '#5b21b6')} />
                <Chip label={`${detail.brand.productsSold}/${detail.brand.products} products sold`} sx={pill('#dcfce7', '#166534')} />
                <Chip label={`Returns ${detail.brand.returnRate}%`} sx={pill('#fee2e2', '#b91c1c')} />
                {detail.brand.avgRating !== null && <Chip label={`${detail.brand.avgRating} ★ (${detail.brand.reviews})`} sx={pill('#fef9c3', '#854d0e')} />}
                <Chip label={`Stock value ${inr(detail.brand.stockValue)}`} sx={pill('#f1f5f9', '#475569')} />
              </Box>
              {detailMini(
                'Top 5 products',
                ['Product', 'Code', 'Units Sold', 'Revenue'],
                detail.topProducts.length === 0
                  ? emptyRow(4, 'No product sales in this period.')
                  : detail.topProducts.map((p) => (
                      <TableRow key={`${p.productCode}-${p.title}`} hover>
                        <TableCell sx={{ fontWeight: 600, fontFamily: FONT }}>{p.title}</TableCell>
                        <TableCell align="right">{p.productCode || '—'}</TableCell>
                        <TableCell align="right">{p.unitsSold}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{inr(p.revenue)}</TableCell>
                      </TableRow>
                    ))
              )}
              {detailMini(
                'Sales by category',
                ['Category', 'Units Sold', 'Revenue', '% of Brand'],
                detail.categorySplit.length === 0
                  ? emptyRow(4, 'No sales in this period.')
                  : detail.categorySplit.map((c) => (
                      <TableRow key={c.title} hover>
                        <TableCell sx={{ fontWeight: 600, fontFamily: FONT }}>{c.title}</TableCell>
                        <TableCell align="right">{c.unitsSold}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{inr(c.revenue)}</TableCell>
                        <TableCell align="right">{c.sharePct}%</TableCell>
                      </TableRow>
                    ))
              )}
              {detailMini(
                'Own vs seller products',
                ['Source', 'Products', 'Units Sold', 'Revenue'],
                detail.sourceSplit.map((s) => (
                  <TableRow key={s.source} hover>
                    <TableCell sx={{ fontWeight: 600, fontFamily: FONT }}>{s.source}</TableCell>
                    <TableCell align="right">{s.products}</TableCell>
                    <TableCell align="right">{s.unitsSold}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{inr(s.revenue)}</TableCell>
                  </TableRow>
                ))
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT }}>Note</DialogTitle>
        <DialogContent dividers>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {(data?.notes || []).map((n, i) => (
              <Typography component="li" key={i} variant="body2" sx={{ mb: 1, fontFamily: FONT }}>{n}</Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrandReport;
